import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@dropbox/dig-components/typography';
import type { TextProps } from '@dropbox/dig-components/typography';

import { getCouponPromotionalPeriodCount } from './coupon-duration';
import { shouldShowFormattedPromotionalMonthlyRate } from './pricing-levels/coupon-rate-text';

export const planMonthlyRateMessages = defineMessages({
  pricePerMonth: {
    id: 'pricing.pricePerMonth',
    description: 'Pricing copy showing a monthly price',
    defaultMessage: '{price} / mo',
  },
  discountedPeriod: {
    id: 'pricing.discountedPeriod',
    description:
      'Pricing period label for a limited-time promotional monthly price',
    defaultMessage: '{count, plural, one {first month} other {first # months}}',
  },
  afterDiscountedPeriod: {
    id: 'pricing.afterDiscountedPeriod',
    description:
      'Pricing copy describing the recurring monthly price after a promotional period',
    defaultMessage: 'then {price} per month',
  },
});

export type PlanMonthlyRateDisplayProps = {
  formattedMonthlyRate: string | null;
  formattedCouponMonthlyPrice?: string | null;
  couponNumMonths?: number | null;
  hasValidCoupon?: boolean;
  size?: TextProps['size'];
  isBold?: boolean;
  className?: string;
  tagName?: TextProps['tagName'];
};

export function PlanMonthlyRateDisplay({
  formattedMonthlyRate,
  formattedCouponMonthlyPrice,
  couponNumMonths,
  hasValidCoupon = false,
  size = 'small',
  isBold = true,
  className,
  tagName = 'p',
}: PlanMonthlyRateDisplayProps) {
  const intl = useIntl();

  if (!formattedMonthlyRate) {
    return null;
  }

  const showPromotionalRate = shouldShowFormattedPromotionalMonthlyRate(
    formattedCouponMonthlyPrice,
    hasValidCoupon,
  );

  if (showPromotionalRate && formattedCouponMonthlyPrice) {
    const count = getCouponPromotionalPeriodCount(
      couponNumMonths ?? undefined,
      'M',
    );

    return (
      <div className={className}>
        <Text variant="paragraph" size={size} tagName={tagName} isBold={isBold}>
          {formattedCouponMonthlyPrice}
        </Text>
        <Text variant="paragraph" size={size} tagName={tagName} color="subtle">
          {intl.formatMessage(planMonthlyRateMessages.discountedPeriod, {
            count,
          })}
        </Text>
        <Text variant="paragraph" size={size} tagName={tagName} color="subtle">
          {intl.formatMessage(planMonthlyRateMessages.afterDiscountedPeriod, {
            price: formattedMonthlyRate,
          })}
        </Text>
      </div>
    );
  }

  return (
    <Text
      variant="paragraph"
      size={size}
      tagName={tagName}
      isBold={isBold}
      className={className}
    >
      {intl.formatMessage(planMonthlyRateMessages.pricePerMonth, {
        price: formattedMonthlyRate,
      })}
    </Text>
  );
}
